// Getting started's "How you'll pay your team" (BL-172): how often the team is paid and the first
// period, the overtime week and rules, and who runs payroll. Pure, so the tests pin the dates and words.
// The step never holds up time tracking: Hours and the Timesheet run on payroll's weeks without it (QA-757).
import { addDays, dayText } from '../books/model.ts';
import { nextPeriod } from '../books/payroll-model.ts';

/** How often the team is paid: a period's length in days, so each next period follows it (nextPeriod). */
export type PayEvery = 7 | 14;
export const PAY_EVERY: readonly (readonly [PayEvery, string])[] = [
  [14, 'Every two weeks'],
  [7, 'Every week'],
];

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;
/** The day of the week a date falls on, 0 for Sunday. */
export const weekdayOf = (date: string) => new Date(`${date}T12:00:00Z`).getUTCDay();

/**
 * The overtime week's choices, the usual one first: Sunday under BC's rules (the Act's week starts on
 * Sunday) and by default; Monday first only when the rules aren't BC's and the pay periods start on a
 * Monday. Every day stays a choice.
 */
export function weekChoices(bc: boolean, periodStartsOn: number | null): (readonly [number, string])[] {
  const all = DAYS.map((d, i) => [i, d] as const);
  return !bc && periodStartsOn === 1 ? [all[1]!, all[0]!, ...all.slice(2)] : all;
}

/** When the pay periods start on another day than the overtime week: a note, so it's no surprise. */
export function periodNote(firstDay: string, weekStartsOn: number): string | null {
  const d = weekdayOf(firstDay);
  return d === weekStartsOn
    ? null
    : `Your pay periods start on ${DAYS[d]}; the overtime week still runs from ${DAYS[weekStartsOn]}.`;
}

/**
 * The first period's first day: as Payroll's own first period (nextPeriod), the period that ends on the
 * last day of this payroll week, for a week's or two weeks' pay.
 */
export function firstDayOf(today: string, weekStartsOn: number, every: PayEvery): string {
  const p = nextPeriod(null, today, weekStartsOn);
  return addDays(p.endDate, 1 - every);
}

/** The first period from its first day: `every` days, paid five days after it ends (as nextPeriod). */
export function firstPeriod(
  firstDay: string,
  every: PayEvery,
): { startDate: string; endDate: string; payDate: string } {
  const endDate = addDays(firstDay, every - 1);
  return { startDate: firstDay, endDate, payDate: addDays(endDate, 5) };
}

/** The step's last line: what saving it does, in one sentence. */
export function saveWords(p: { startDate: string; endDate: string } | null, provider: string): string {
  const who = provider.trim() ? ` ${provider.trim()} runs payroll.` : '';
  return p
    ? `Opens the first pay period, ${dayText(p.startDate)} to ${dayText(p.endDate)}, and keeps these rules.${who}`
    : `Keeps these rules.${who}`;
}
